import { cloneDeep } from 'lodash'
import { where } from 'firebase/firestore'
import { v4 } from 'uuid'

import { calculateAge, getConvertedData } from '../models/interface.helper'
import { getUserFullName } from './helpers'

import FirestoreService from '../services/firestoreService'
import UserService from '../services/firebase-service/users/userService'

import { RiderTeamMemberModel } from '../models/rider-team-member/riderTeamMember.model'

import { IRiderTeamMemberInterface } from '../models/rider-team-member/riderTeamMember.interface'
import {
  IPaperworkTab_,
  IRegistrationTabs,
  ITeamTab,
  TEventRegisteredUsers,
} from '../models/event-registered-users/event-registered-users.interface'
import { IUserInterface } from '../models/users/user.interface'
import { IUserPublicInterface } from '../models/users-public/users-public.interface'
import { IUserInterfaceTeamRegistrar } from '../components/modals/InfoModals/components/tabs/TeamRegistrarTab/TeamRegistrarTab'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IPaperworkTeam, IUserDocument } from '../models/user-documents/user-documents.interface'

import { signedStatuses } from '../const/data/v01/rider-team-members/firestore-v01-rider-team-member-const'
import { CONST } from '../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

export function mergeRidersTeamMembersWithPaperworkTabData({
  paperworkTabData,
  ridersTeamMembers,
}: {
  paperworkTabData: IRegistrationTabs['IPaperworkTab'][]
  ridersTeamMembers: IRiderTeamMemberInterface[]
}) {
  let paperworkTabData_ = cloneDeep(paperworkTabData)
  let ridersTeamMembers_ = cloneDeep(ridersTeamMembers)

  paperworkTabData_ = paperworkTabData_.map((currPaperworkRow) => {
    const foundRiderTeamMembers = ridersTeamMembers_.filter(
      (currRidersTeamMember) =>
        currPaperworkRow.registrationDocId === currRidersTeamMember.registrationDocId &&
        currPaperworkRow.riderId === currRidersTeamMember.riderId &&
        currRidersTeamMember.horseId === currPaperworkRow.horseId
    )

    return {
      ...currPaperworkRow,
      ridersTeamMembers: foundRiderTeamMembers,
    }
  })

  return paperworkTabData_
}

export async function getRiderTeamMembersFromDb(registrationId: string) {
  let riderTeamMembers: IRiderTeamMemberInterface[] = []
  try {
    const riderTeamMemberShots = await FirestoreService.filterItems(
      COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
      [where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId)]
    )

    if (riderTeamMemberShots.size) {
      riderTeamMemberShots.forEach((currDoc) => {
        riderTeamMembers.push(
          getConvertedData(RiderTeamMemberModel.fromFirestoreDoc(currDoc).toObject())
        )
      })
    }
  } catch (error) {
    console.error(error, 'error')
  } finally {
    return riderTeamMembers
  }
}

export const getTeamMemberById = (registrar: IUserInterface, teamTabData: ITeamTab, id: string) => {
  const userAsTeamMember = {
    memberId: registrar?.id,
    memberName: registrar ? getUserFullName(registrar) : null,
    memberProfilePicture: registrar?.userProfilePicture || null,
  }
  return [userAsTeamMember, ...teamTabData].find((member) => member.memberId === id)
}

export const getRegisterTeamData = async ({
  teamTabData,
  userId,
}: {
  teamTabData: ITeamTab
  userId: string
}) => {
  const userTeamMembers: IUserPublicInterface[] = []
  const competitor: IUserPublicInterface = await UserService.getUserById(userId)

  const ids: Set<string> = new Set()
  ;[...(competitor.userTeamMembers || []), ...teamTabData].forEach((member) => {
    if (member.memberId) ids.add(member.memberId)
  })

  const users: IUserPublicInterface[] = await UserService.getByIds([...ids])

  ids.forEach((id) => {
    const current = users.find((user) => user.id === id)
    const isExist = userTeamMembers.find((user) => user.id === id)
    if (current && !isExist) userTeamMembers.push(current)
  })

  return { userTeamMembers, competitor }
}

export const sortTeamMembersByRegistrar = (
  teamMembers: IUserInterfaceTeamRegistrar[],
  registrarId: string
) => {
  return teamMembers.sort(
    (a, b) =>
      (a.id === registrarId ? 0 : a.teamMemberRole === 'Rider' ? 1 : 2) -
      (b.id === registrarId ? 0 : b.teamMemberRole === 'Rider' ? 1 : 2)
  )
}

export const getTeamMemberObject = ({
  eventRegisteredUser,
  paperwork,
  teamMember,
}: {
  eventRegisteredUser: TEventRegisteredUsers | null
  paperwork: IPaperworkTab_
  teamMember: {
    teamMemberId: string | null
    teamMemberDob: string | Date | null
    teamMemberName: string | null
    teamMemberRole: string | null
    teamMemberEmail: string | null
    teamMemberProfilePicture: string | null
  }
}) => {
  return {
    id: `${v4()}-${Date.now()}`,

    mailLog: [],
    userId: eventRegisteredUser?.userId || null,

    eventId: eventRegisteredUser?.eventId || null,
    eventName: eventRegisteredUser?.eventName || null,
    registrationDocId: eventRegisteredUser?.id || null,

    riderDob: paperwork.riderDob || null,
    riderId: paperwork.riderId || null,
    riderName: paperwork.riderName || null,
    riderNameNGram: paperwork.riderNameNGram || [],
    riderEmail: paperwork.riderEmail || null,
    riderProfilePicture: paperwork.riderProfilePicture || null,
    riderCountry: paperwork.riderCountry || null,

    ...teamMember,

    horseId: paperwork.horseId,
    horseName: paperwork.horseName,
    horseProfilePicture: paperwork.horseProfilePicture || null,

    modified: new Date().toString(),
    created: new Date().toString(),
  }
}

export const getRiderTeamMembers = (
  rider: IRegistrationByDayInterface,
  riderTeamMembers: IRiderTeamMemberInterface[],
  usersDocuments: IUserDocument[]
) => {
  let newMembersData: IPaperworkTeam[] = []

  riderTeamMembers.forEach((riderTeamMember) => {
    const currentDocuments: IUserDocument[] = []
    usersDocuments.forEach((usersDocument) => {
      if (
        usersDocument.signatoryId === riderTeamMember.teamMemberId &&
        usersDocument.signatoryDefaultRole === riderTeamMember.teamMemberRole &&
        usersDocument.horseId === riderTeamMember.horseId
      ) {
        currentDocuments.push(usersDocument)
      }
    })

    const isExist = newMembersData.find(
      (newMember) =>
        newMember.teamMemberId === riderTeamMember.teamMemberId &&
        newMember.teamMemberRole === riderTeamMember.teamMemberRole &&
        newMember.horseId === riderTeamMember.horseId
    )
    if (!isExist)
      newMembersData.push({
        ...riderTeamMember,
        documents: currentDocuments,
      })
  })

  newMembersData = newMembersData.filter(
    (item) => item.riderId === rider.riderId && item.horseId === rider.horseId
  )

  return newMembersData
}

export const isValidTeamMember = (teamMembers: IPaperworkTeam[]) => {
  let issues = 0
  teamMembers.forEach((item) => {
    if (item.documents?.[0]) {
      const notSigned = item.documents.filter(
        (document) => document.status === signedStatuses.NOT_SIGNED
      )
      issues += +notSigned.length
    }
    const age = calculateAge(item?.teamMemberDob)
    const isRider = item.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.RIDER
    const guardian = teamMembers.find(
      (teamMember) => teamMember.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.GUARDIAN
    )

    if (age < 18 && isRider && !guardian) issues += 1
  })
  return issues
}

export const haveTeamMember = (
  teamMembers: (IPaperworkTeam | IRiderTeamMemberInterface)[],
  role: string
) => {
  return teamMembers.some((item) => item.teamMemberRole === role)
}
